import { PaletteColorOptions, createTheme, alpha, PaletteColor } from '@mui/material/styles';

import './fonts.css';

declare module '@mui/material/styles' {
  interface Theme {
    custom: Record<string, string>;
  }

  interface ThemeOptions {
    custom?: Record<string, string>;
  }

  interface TypeBackground {
    grey?: string;
  }

  interface TypeText {
    tertiary?: string;
  }

  interface Palette {
    tertiary: PaletteColor;
    neutral: PaletteColor;
    colors: Record<string, string>;
    charts: {
      environment: string[];
      social: string[];
      governance: string[];
      legends: string[];
    };
  }

  interface PaletteOptions {
    tertiary?: PaletteColorOptions;
    neutral?: PaletteColorOptions;
    colors?: Record<string, string>;
    charts?: {
      environment: string[];
      social: string[];
      governance: string[];
      legends: string[];
    };
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#4356ed',
      light: alpha('#4356ed', 0.2),
      contrastText: '#fff',
    },
    secondary: {
      main: '#fdc535',
    },
    tertiary: {
      main: '#B5D065',
    },
    info: {
      main: '#2941FF',
    },
    success: {
      main: '#10B981',
      light: '#D9F5ED',
      dark: '#047857',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#E6A800',
      light: '#FFF3CC',
      dark: '#B38600',
      contrastText: '#000000',
    },
    error: {
      main: '#FF8080',
      light: '#FFCBBF',
      dark: '#FF8080',
      contrastText: '#FFFFFF',
    },
    neutral: {
      // bischen dunkler
      main: '#68677F',
      light: '#F0F2FF',
      dark: '#333652',
      contrastText: '#FFFFFF',
    },
    colors: {
      almond: '#F7DECA',
      aquaIsland: '#A5D8DD',
      bleuDeFrance: '#3D84E1',
      blueberry: '#4047AB',
      cadetBlue: '#B2B3C1',
      cascade: '#82AC9F',
      casal: '#275869',
      casper: '#A7BDC6',
      chocolate: '#8E482D',
      citrineWhite: '#F9F6D3',
      cornflowerBlue: '#6E94E5',
      darkGrey: '#707070',
      denimBlue: '#78B4F1',
      flatBlue: '#3572AE',
      ghost: '#C7CDD9',
      goldenRod: '#FAD372',
      greenPea: '#295D47',
      greenishCyan: '#CCF4EA',
      greenishYellow: '#DBE6BB',
      grey: '#030229',
      greyChateau: '#9BA8AD',
      gullGrey: '#96ACC4',
      gunMetal: '#26313A',
      gunPowder: '#3E4163',
      iceBerg: '#D6EEFB',
      jaggedIce: '#C1E9DD',
      jellyfish: '#4DD0A8',
      leafyGreen: '#51B133',
      lightBlue: '#C7EAE7',
      lightYellow: '#F9F6D3',
      lightCornflowerBlue: '#98CDEC',
      lightGreen: '#CEEAD3',
      lightGrey: '#68677F',
      lightNavy: '#1C4E80',
      lightPurple: '#DEE0F8',
      mediumSpringBud: '#CADD90',
      mistBlue: '#656782',
      nebula: '#CDDED9',
      opal: '#A7C5BC',
      paleAqua: '#B6D8E4',
      paleGreen: '#D8F6C7',
      paleOliveGreen: '#B5D065',
      pearlAqua: '#82D4BB',
      pictonBlue: '#51B4E6',
      purple: '#6a3d9a',
      regentGrey: '#829298',
      rhino: '#333652',
      salmonTransparent: alpha('#FF8080', 0.05),
      slateGrey: '#6F7C8B',
      softGrey: '#EDEDED',
      softGreyTransparent: alpha('#EDEDED', 0.3),
      summerGreen: '#82C09A',
      summerLightGreen: '#D1EFE8',
      tealishBlue: '#D3DEFC',
      white: '#FFFFFF',
      lightSmokeGrey: '#D9D6D680',
      lightSmokeGreyTransparent: alpha('#D9D6D6', 0.3),
      lightMainBlue: '#4356ED33',
      softLightMainBlue: '#4356ED66',
      softPrimary: alpha('#4356ed', 0.2),
      verySoftPrimary: alpha('#4356ed', 0.05),
      lightPrimary: '#F0F2FF',
      shadeLightGrey: '#BEBEBD',
      shadedSmokeGrey: '#D9D9D9',
      softOrange: alpha('#FFD79C', 0.7),
      black: '#000000',
      paleGrey: '#F6F6F6',
      softBlack: '#1B2A81',
      lightSoftGrey: '#ADB2D2',
      darkBlue: '#394DF4',
      lightBlueTransparent: '#B0B8FB',
      lightSoftSmoothGrey: '#E1E1E5',
      milkWhite: '#F2F2F2',
      periwinkle: '#C6CDFF',
      lightRose: '#FFCCCF',
      waterLeaf: '#B3E4D8',
      softWhite: '#efecec',
      watermelonSugar: '#F07474',
      paleSpring: '#F0E9B4',
      glossyRed: '#EC5454',
      glodenYellow: '#C79C02',
      lightGoldenYellow: '#FFF9B4',
      lightGlossyRed: '#FFE6E6',
    },
    background: {
      default: '#F8F9FB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1E1E1E',
      secondary: '#68677F',
      disabled: '#D9D6D6',
    },
    divider: '#D9D6D6',
    charts: {
      environment: ['#A5D8DD', '#7E909A', '#1C4E80', '#5C95AE', '#4E4E6A', '#D2D6D9', '#97B1C6'],
      social: ['#333652', '#E9EAEC', '#90ADC6', '#FAD02C'],
      governance: ['#7E909A'],
      legends: [
        '#8ECAE6',
        '#BFD8B8',
        '#FFD3B6',
        '#B19CD9',
        '#DD7596',
        '#809BCE',
        '#95B8D1',
        '#B8E0D2',
        '#D6EADF',
        '#EAC4D5',
        '#8AC0DE',
        '#F0C5D5',
        '#F5C9B2',
        '#EAB159',
        '#9E6B55',
      ],
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: ['Inter', 'Questrial', 'Arial', 'sans-serif'].join(','),
    h1: {
      fontFamily: 'Inter',
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontFamily: 'Inter',
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontFamily: 'Inter',
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontFamily: 'Inter',
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontFamily: 'Inter',
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontFamily: 'Inter',
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    body1: {
      fontFamily: 'Inter',
      fontSize: '1rem',
      fontWeight: 500,
    },
    body2: {
      fontFamily: 'Inter',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: 'Inter',
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: 'Inter',
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    caption: {
      fontFamily: 'Inter',
      fontSize: '0.75rem',
      fontWeight: 600,
    },
    overline: {
      fontFamily: 'Inter',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: 'Inter',
      fontSize: '0.875rem',
      fontWeight: 600,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1500,
    },
  },
  custom: {
    drawerWidth: '280px',
    drawerWidthClosed: '96px',
    appBarHeight: '80px',
    border: '1px solid #D9D6D6',
    thinBorder: '0.05px solid #D9D6D6',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.16)',
    backdropBlur: 'blur(5px)',
    inputLabels: 'scale(1) translate(0, -26px)',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          // Smaller than in design specs (56px)
          height: '46px',
          borderRadius: '5px',
          textTransform: 'none',
        },
        sizeSmall: {
          // Smaller than in design specs (42px)
          height: '38px',
        },
        textSizeSmall: {
          fontSize: '0.875rem',
          fontFamily: 'Inter',
          fontWeight: 600,
        },
        textSizeLarge: {
          fontSize: '0.9375rem',
          fontFamily: 'Inter',
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'text.secondary',
          borderRadius: '10px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: '10px', border: '1px solid #D9D6D6' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#EDEDED',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #D9D6D6',
          fontSize: '0.875rem',
          height: '60px',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiToolbar-root': {
            width: 'fit-content',
          },
          '& .MuiTablePagination-select': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.16)',
          borderRadius: '10px',
          border: '1px solid #D90D6D6',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F9FB',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontFamily: 'Inter',
          fontWeight: 600,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          height: '1.2rem',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        root: {
          backgroundColor: 'white',
          height: '50px',
        },
        sizeSmall: {
          height: '40px',
          fontSize: '1rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#D9D6D6',
            borderWidth: '0.5px',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
          padding: '0 16px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          height: '32px',
          fontSize: '1rem',
          color: alpha('#1E1E1E', 0.6),
          fontWeight: 400,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'scale(1) translate(0, -26px)',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: 1.5,
          color: 'neutral.main',
          '&.Mui-focused': {
            color: '#68677F',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: 'rgba(3,2,41,0.6)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.16)',
          backgroundColor: 'white',
          color: 'rgba(3,2,41,0.6)',
        },
        action: {
          size: '20px',
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          marginLeft: '-10px',
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          height: '4px',
        },
        light: {
          color: '#D9D6D6',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&.Mui-expanded': {
            margin: '0',
          },
          ':before': {
            height: 0,
          },
        },
      },
    },
  },
});
// Separate these from the custom object, because we want to keep the original objects as well
const shadows = ['0 2px 10px 1px rgba(0, 0, 0, 0.1)'];

theme.shadows.push(...shadows);

export default theme;
